<template>
  <div class="profile-wrapper">
    <!-- Header Section -->
    <div class="profile-hero">
      <div class="hero-content">
        <div class="avatar-section">
          <editable-avatar
            class="modern-avatar"
            :image="profile.midThumbnail ? profile.midThumbnail : profile.image"
            :onImageSelected="onImageSelected"
            size="140"
          />
          <!-- <div class="avatar-overlay">
            <v-icon color="white" size="24">mdi-camera</v-icon>
          </div> -->
        </div>
        <div class="hero-text">
          <h1 class="hero-title">Business Profile</h1>
          <p class="hero-subtitle">Manage your company informations</p>
        </div>
      </div>
      <div class="hero-decoration">
        <div class="decoration-circle circle-1"></div>
        <div class="decoration-circle circle-2"></div>
        <div class="decoration-circle circle-3"></div>
      </div>
    </div>

    <!-- Form Container -->
    <v-container class="form-container">
      <v-card class="profile-form-card" flat>
        <v-card-text class="pa-0">
          <v-form ref="form" v-model="formValid" class="modern-form">
            
  <!-- Company Information Section -->
  <div class="form-section">
    <div class="section-header">
      <v-icon color="primary" class="section-icon">mdi-domain</v-icon>
      <h3 class="section-title">Company Information</h3>
    </div>
    
    <v-row class="form-row">
      <v-col cols="12" lg="8">
        <div class="input-group">
          <label class="input-label">Business Name</label>
          <v-text-field
            v-model="companyName"
            :rules="[
              v => !!v || 'Business Name is required',
              v => (v && v.length <= 100) || 'Name must be less than 100 characters'
            ]"
            counter="100"
            solo
            flat
            class="modern-input"
            placeholder="Enter your business name"
            hide-details="auto"
          >
            <template v-slot:prepend-inner>
              <v-icon color="#6B7280">mdi-office-building</v-icon>
            </template>
          </v-text-field>
        </div>
      </v-col>
      <v-col cols="12" lg="4">
        <div class="input-group">
          <label class="input-label">Business Email</label>
        <v-text-field
            v-model="email"
            :rules="[
              v => !!v || 'Email is required',
              v => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(v) || 'E-mail must be valid',
              v => (v && v.length <= 100) || 'Email must be less than 100 characters'
            ]"
            counter="100"
            solo
            flat
            class="modern-input"
            placeholder="company@example.com"
            hide-details="auto"
            @blur="email = email ? email.toLowerCase() : ''"
          >
            <template v-slot:prepend-inner>
              <v-icon color="#6B7280">mdi-email</v-icon>
            </template>
          </v-text-field>
        </div>
      </v-col>
    </v-row>
  </div>

  <!-- Contact Information Section -->
  <div class="form-section">
    <div class="section-header">
      <v-icon color="primary" class="section-icon">mdi-account-group</v-icon>
      <h3 class="section-title">Contact Information</h3>
    </div>
    
    <v-row class="form-row">
      <v-col cols="12" md="6">
        <div class="input-group">
          <label class="input-label">Contact Person</label>
          <v-text-field
            v-model="contactPerson"
            :rules="[
              v => !!v || 'Contact Person is required',
              v => (v && v.length <= 50) || 'Name must be less than 50 characters',
              v => /^[a-zA-Z\s'-]+$/.test(v) || 'Only letters and spaces allowed'
            ]"
            counter="50"
            solo
            flat
            class="modern-input"
            placeholder="Full name"
            hide-details="auto"
          >
            <template v-slot:prepend-inner>
              <v-icon color="#6B7280">mdi-account</v-icon>
            </template>
          </v-text-field>
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <div class="input-group">
          <label class="input-label">Job Title</label>
          <v-text-field
            v-model="contactTitle"
            :rules="[
              v => !!v || 'Contact Title is required',
              v => (v && v.length <= 50) || 'Title must be less than 50 characters'
            ]"
            counter="50"
            solo
            flat
            class="modern-input"
            placeholder="Position or role"
            hide-details="auto"
          >
            <template v-slot:prepend-inner>
              <v-icon color="#6B7280">mdi-badge-account</v-icon>
            </template>
          </v-text-field>
        </div>
      </v-col>
    </v-row>

    <v-row class="form-row">
      <v-col cols="12" md="6">
        <div class="input-group">
          <label class="input-label">Phone Number</label>
          <v-text-field
  v-model="phoneNumber"
  :rules="[
    v => !!v || 'Phone Number is required',
    v => {
      const digits = v ? v.replace(/\D/g, '') : '';
      return digits.length === 10 || 'Phone number must be exactly 10 digits';
    }
  ]"
  solo
  flat
  class="modern-input"
  placeholder="(555) 123-4567"
  hide-details="auto"
  @input="formatPhoneNumber"
  @keypress="onlyNumberInput" 
>
  <template v-slot:prepend-inner>
    <v-icon color="#6B7280">mdi-phone</v-icon>
  </template>
</v-text-field>
        </div>
      </v-col>
    </v-row>
  </div>

  <!-- Address Information Section -->
  <div class="form-section">
    <div class="section-header">
      <v-icon color="primary" class="section-icon">mdi-map-marker</v-icon>
      <h3 class="section-title">Business Address</h3>
    </div>
    
    <v-row class="form-row">
      <v-col cols="12">
        <div class="input-group">
          <label class="input-label">Street Address</label>
          <v-text-field
            v-model="address"
            :rules="[
              v => !!v || 'Address is required',
              v => (v && v.length <= 100) || 'Address must be less than 100 characters'
            ]"
            counter="100"
            solo
            flat
            class="modern-input"
            placeholder="123 Business Street"
            hide-details="auto"
          >
            <template v-slot:prepend-inner>
              <v-icon color="#6B7280">mdi-home</v-icon>
            </template>
          </v-text-field>
        </div>
      </v-col>
    </v-row>

    <v-row class="form-row">
      <v-col cols="12" md="5">
        <div class="input-group">
          <label class="input-label">City</label>
          <v-text-field
            v-model="city"
            :rules="[
              v => !!v || 'City is required',
              v => (v && v.length <= 50) || 'City must be less than 50 characters',
              v => /^[a-zA-Z\s-]+$/.test(v) || 'Invalid city name'
            ]"
            counter="50"
            solo
            flat
            class="modern-input"
            placeholder="City name"
            hide-details="auto"
          >
            <template v-slot:prepend-inner>
              <v-icon color="#6B7280">mdi-city</v-icon>
            </template>
          </v-text-field>
        </div>
      </v-col>
      <v-col cols="12" md="4">
        <div class="input-group">
          <label class="input-label">State</label>
          <v-select
            v-model="state"
            :items="stateOptions"
            :rules="[v => !!v || 'State is required']"
            solo
            flat
            class="modern-input"
            placeholder="Select state"
            hide-details="auto"
          >
            <template v-slot:prepend-inner>
              <v-icon color="#6B7280">mdi-map</v-icon>
            </template>
          </v-select>
        </div>
      </v-col>
      <v-col cols="12" md="3">
        <div class="input-group">
          <label class="input-label">ZIP Code</label>
          <v-text-field
            v-model="zip"
            :rules="[
              v => !!v || 'Zip Code is required',
              v => /^\d{5}(-\d{4})?$/.test(v) || 'Invalid ZIP code format (e.g. 12345 or 12345-6789)'
            ]"
            solo
            flat
            class="modern-input"
            placeholder="12345"
            hide-details="auto"
          >
            <template v-slot:prepend-inner>
              <v-icon color="#6B7280">mdi-postage-stamp</v-icon>
            </template>
          </v-text-field>
        </div>
      </v-col>
    </v-row>
  </div>

            <!-- Action Buttons -->
            <div class="action-section">
              <div class="action-buttons">
                <v-btn
                  large
                  outlined
                  color="grey darken-1"
                  class="cancel-btn"
                  @click="resetForm"
                >
                  <v-icon left>mdi-refresh</v-icon>
                  Reset
                </v-btn>
                <v-btn
                  large
                  color="#1e658d"
                  class="save-btn"
                  :disabled="!isFormValid"
                  :loading="isLoading"
                  @click="updateProfile"
                >
                  <v-icon left>mdi-content-save</v-icon>
                  Save Changes
                </v-btn>
              </div>
            </div>

          </v-form>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- Success/Error Notification -->
    <v-snackbar
      v-model="snackbar"
      :timeout="4000"
      top
      right
      :color="snackbarColor"
      shaped
    >
      <div class="notification-content">
        <v-icon left color="white">
          {{ snackbarIcon }}
        </v-icon>
        <span>{{ message }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn
          color="white"
          text
          v-bind="attrs"
          @click="snackbar = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import EditableAvatar from "@/components/EditableAvatar.vue";

export default {
  components: {
    EditableAvatar
  },
  data() {
    return {
      companyName: "",
      email: "",
      contactPerson: "",
      contactTitle: "",
      phoneNumber: "",
      address: "",
      city: "",
      state: "",
      zip: "",
      selectedImage: null,
      formValid: false,
      snackbar: false,
      message: "",
      isLoading: false,
      stateOptions: [
        "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
        "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
        "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
        "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
        "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
        "AS", "DC", "FM", "GU", "MH", "MP", "PW", "PR", "VI"
      ]
    };
  },
  computed: {
    ...mapState("auth", {
      profile: "profile"
    }),
    isFormValid() {
      return (
        this.companyName &&
        this.contactPerson &&
        this.contactTitle &&
        this.email &&
        this.phoneNumber &&
        this.address &&
        this.city &&
        this.state &&
        this.zip
      );
    },
    snackbarColor() {
      return this.message && this.message.toLowerCase().includes('success') ? 'success' : 'error';
    },
    snackbarIcon() {
      return this.message && this.message.toLowerCase().includes('success') ? 'mdi-check-circle' : 'mdi-alert-circle';
    }
  },
  watch: {
    profile: {
      handler(newProfile) {
        if (newProfile) {
          this.companyName = newProfile.name || "";
          this.contactPerson = newProfile.contact_person || "";
          this.contactTitle = newProfile.contact_title || "";
          this.email = newProfile.email || "";
          this.phoneNumber = newProfile.phone || "";
          this.address = newProfile.address || "";
          this.city = newProfile.city || "";
          this.state = newProfile.state || "";
          this.zip = newProfile.zip || "";
        }
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions("company", ["updateCompany"]),
    onImageSelected(image) {
      this.selectedImage = image;
    },
        async validateForm() {
      const validations = await Promise.all([
        this.$refs.form.validate(),
        // Add any additional validations here
      ]);
      
      return validations.every(v => v);
    },
      formatPhoneNumber() {
    // Remove all non-digit characters
    let digits = this.phoneNumber.replace(/\D/g, '');
    
    // Limit to 10 digits
    digits = digits.substring(0, 10);
    
    // Format as (XXX) XXX-XXXX
    if (digits.length > 0) {
      let formatted = '(' + digits.substring(0, 3);
      if (digits.length > 3) {
        formatted += ') ' + digits.substring(3, 6);
      }
      if (digits.length > 6) {
        formatted += '-' + digits.substring(6, 10);
      }
      this.phoneNumber = formatted;
    } else {
      this.phoneNumber = '';
    }
  },
  onlyNumberInput(e) {
    // Allow: backspace, delete, left/right arrows, tab, etc.
    if (
      e.key.length === 1 &&
      !/[0-9]/.test(e.key)
    ) {
      e.preventDefault();
    }
  },
    async updateProfile() {
   const isValid = await this.validateForm();
      if (!isValid) {
        this.message = "Please fix all validation errors before submitting";
        this.snackbar = true;
        return;
      }

      this.isLoading = true;

      const formData = new FormData();
      formData.append("name", this.companyName);
      formData.append("email", this.email);
      formData.append("address", this.address);
      formData.append("city", this.city);
      formData.append("state", this.state);
      formData.append("zip", this.zip);
      formData.append("phone", this.phoneNumber);
      formData.append("contact_person", this.contactPerson);
      formData.append("contact_title", this.contactTitle);
      if (this.selectedImage) {
        formData.append("image", this.selectedImage);
      }

      try {
        await this.updateCompany(formData);
        this.message = "Profile updated successfully!";
        this.$router.push('/')
        this.snackbar = true;
      } catch (error) {
        this.message = error.message || "Failed to update profile";
        this.snackbar = true;
        console.error("Error updating profile:", error);
      } finally {
        this.isLoading = false;
      }
    },
    resetForm() {
      this.$refs.form.reset();
      if (this.profile) {
        this.companyName = this.profile.name || "";
        this.contactPerson = this.profile.contact_person || "";
        this.contactTitle = this.profile.contact_title || "";
        this.email = this.profile.email || "";
        this.phoneNumber = this.profile.phone || "";
        this.address = this.profile.address || "";
        this.city = this.profile.city || "";
        this.state = this.profile.state || "";
        this.zip = this.profile.zip || "";
      }
    }
  }
};
</script>

<style scoped lang="scss">
.profile-wrapper {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.profile-hero {
  position: relative;
  background: linear-gradient(135deg,#084468,#0a5b8a);
  padding: 60px 0 80px;
  overflow: hidden;
  
  .hero-content {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
    display: flex;
    align-items: center;
    gap: 40px;
    position: relative;
    z-index: 2;
    
    @media (max-width: 768px) {
      flex-direction: column;
      text-align: center;
      gap: 30px;
    }
  }
  
  .avatar-section {
    position: relative;
    
    .modern-avatar {
      border: 4px solid rgba(255, 255, 255, 0.2);
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
      transition: all 0.3s ease;
      
      &:hover {
        transform: translateY(-5px);
        box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
      }
    }
    
    .avatar-overlay {
      position: absolute;
      bottom: 10px;
      right: 10px;
      background: rgba(0, 0, 0, 0.6);
      border-radius: 50%;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.3s ease;
      
      &:hover {
        background: rgba(0, 0, 0, 0.8);
        transform: scale(1.1);
      }
    }
  }
  
  .hero-text {
    color: white;
    
    .hero-title {
      font-size: 3rem;
      font-weight: 700;
      margin-bottom: 10px;
      line-height: 1.2;
      
      @media (max-width: 768px) {
        font-size: 2.5rem;
      }
    }
    
    .hero-subtitle {
      font-size: 1.2rem;
      opacity: 0.9;
      margin: 0;
      font-weight: 300;
    }
  }
  
  .hero-decoration {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    
    .decoration-circle {
      position: absolute;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.1);
      
      &.circle-1 {
        width: 200px;
        height: 200px;
        top: -100px;
        right: -100px;
      }
      
      &.circle-2 {
        width: 150px;
        height: 150px;
        bottom: -75px;
        left: -75px;
      }
      
      &.circle-3 {
        width: 100px;
        height: 100px;
        top: 50%;
        right: 10%;
        transform: translateY(-50%);
      }
    }
  }
}

.form-container {
  max-width: 1000px;
  margin-top: -40px;
  position: relative;
  z-index: 3;
  padding-bottom: 60px;
}

.profile-form-card {
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  background: white;
  overflow: hidden;
}

.modern-form {
  padding: 40px;
}

.form-section {
  margin-bottom: 40px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  .section-header {
    display: flex;
    align-items: center;
    margin-bottom: 24px;
    padding-bottom: 12px;
    border-bottom: 2px solid #f3f4f6;
    
    .section-icon {
      margin-right: 12px;
      font-size: 24px;
    }
    
    .section-title {
      font-size: 1.25rem;
      font-weight: 600;
      color: #374151;
      margin: 0;
    }
  }
}

.form-row {
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
}

.input-group {
  margin-bottom: 20px;
  
  .input-label {
    display: block;
    font-size: 0.875rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 8px;
  }
}

.modern-input {
  ::v-deep .v-input__slot {
    border-radius: 12px !important;
    border: 2px solid #e5e7eb !important;
    background: #f9fafb !important;
    box-shadow: none !important;
    min-height: 56px;
    transition: all 0.3s ease;
    
    &:hover {
      border-color: #d1d5db !important;
      background: #ffffff !important;
    }
  }
  
  ::v-deep .v-input--is-focused .v-input__slot {
    border-color: #6366f1 !important;
    background: #ffffff !important;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1) !important;
  }
  
  ::v-deep .v-text-field__details {
    margin-top: 4px;
    padding-left: 4px;
  }
  
  ::v-deep input {
    font-size: 1rem;
    color: #374151;
    
    &::placeholder {
      color: #9ca3af;
    }
  }
}

.action-section {
  margin-top: 48px;
  padding-top: 32px;
  border-top: 2px solid #f3f4f6;
  
  .action-buttons {
    display: flex;
    gap: 16px;
    justify-content: flex-end;
    
    @media (max-width: 600px) {
      flex-direction: column;
      align-items: stretch;
    }
  }
}

.cancel-btn {
  border-radius: 12px !important;
  height: 48px !important;
  padding: 0 24px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.025em !important;
  transition: all 0.3s ease !important;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
}

.save-btn {
  border-radius: 12px !important;
  height: 48px !important;
  padding: 0 32px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.025em !important;
  box-shadow: 0 4px 14px 0 rgba(99, 102, 241, 0.3) !important;
  transition: all 0.3s ease !important;
  color: #fff;
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(99, 102, 241, 0.4) !important;
  }
  
  &:disabled {
    opacity: 0.6 !important;
  }
}

.notification-content {
  display: flex;
  align-items: center;
  font-weight: 500;
}

@media (max-width: 960px) {
  .modern-form {
    padding: 30px 20px;
  }
  
  .form-container {
    padding: 0 16px 40px;
  }
  
  .profile-hero {
    padding: 40px 0 60px;
  }
}

@media (max-width: 600px) {
  .modern-form {
    padding: 20px 16px;
  }
  
  .profile-hero {
    padding: 30px 0 50px;
    
    .hero-text .hero-title {
      font-size: 2rem;
    }
  }
}
</style>


